import { explicitMaybe, stringLiteral } from '@execonline-inc/decoders';
import { Decoder, array, boolean, field, number, oneOf, string, succeed } from 'jsonous';
import { resourceDecoder } from '../../../Resource/Decoders';
import { formTemplatesDecoder } from '../../../Resource/FormTemplates/Decoders';
import {
  experienceResourcesDecoder,
  productCollectionResourceDecoder,
  productLicenseResourceDecoder,
} from '../Decoders';
import { showUnlicensedToggleDecoder } from './CurrentCollectionStep/Builder/ExperienceCatalog/Decoders';
import { learningCollectionPublishResourceDecoder } from './Edit/CurrentCollectionStep/Share/PublishSection/Store/Decoders';
import {
  CommerceType,
  LearningCollectionEditFormPayload,
  LearningCollectionEditFormResource,
  PermissionType,
} from './Edit/Store/Types';
import {
  BrowseCatalog,
  LearningCollectionNewFormPayload,
  LearningCollectionNewFormResource,
} from './Types';

const browseCatalogDecoder: Decoder<BrowseCatalog> = oneOf([
  stringLiteral<BrowseCatalog>('displayed'),
  stringLiteral<BrowseCatalog>('hidden'),
]);

const learningCollectionNewFormPayloadDecoder: Decoder<LearningCollectionNewFormPayload> = succeed(
  {},
)
  .assign('kind', field('kind', stringLiteral('new')))
  .assign('availableExtensionIds', field('available_extension_ids', array(number)))
  .assign('allExperiences', field('all_experiences', experienceResourcesDecoder))
  .assign('browseCatalog', field('browse_catalog', browseCatalogDecoder))
  .assign('warningStatus', field('warning_status', showUnlicensedToggleDecoder));

export const learningCollectionNewFormResourceDecoder: Decoder<LearningCollectionNewFormResource> =
  resourceDecoder(learningCollectionNewFormPayloadDecoder).assign('templates', ({ links }) =>
    field('templates', formTemplatesDecoder(links)),
  );

const commerceTypeDecoder: Decoder<CommerceType> = oneOf([
  stringLiteral<CommerceType>('not-available'),
  stringLiteral<CommerceType>('cc-only'),
  stringLiteral<CommerceType>('cc-or-direct-bill'),
]);

const permissionTypeDecoder: Decoder<PermissionType> = oneOf([
  stringLiteral<PermissionType>('private'),
  stringLiteral<PermissionType>('organization'),
  stringLiteral<PermissionType>('client-groups'),
]);

const learningCollectionEditFormPayloadDecoder: Decoder<LearningCollectionEditFormPayload> =
  succeed({})
    .assign('kind', field('kind', stringLiteral('edit')))
    .assign('id', field('id', number))
    .assign('name', field('name', string))
    .assign('description', field('description', string))
    .assign('commerceType', field('commerce_type', commerceTypeDecoder))
    .assign('permissionType', field('permission_type', permissionTypeDecoder))
    .assign('clientGroups', field('client_groups', array(number)))
    .assign('publishing', field('publishing', learningCollectionPublishResourceDecoder))
    .assign(
      'productCollections',
      field('product_collections', array(productCollectionResourceDecoder)),
    )
    .assign('experiences', field('experiences', experienceResourcesDecoder))
    .assign('allExperiences', field('all_experiences', experienceResourcesDecoder))
    .assign('browseAll', field('browse_all', boolean))
    .assign('availableExtensionIds', field('available_extension_ids', array(number)))
    .assign('browseCatalog', field('browse_catalog', browseCatalogDecoder))
    .assign(
      'productLicense',
      field('product_license', explicitMaybe(productLicenseResourceDecoder)),
    )
    .assign('selectedScheduledPrograms', field('selected_scheduled_programs', array(number)))
    .assign('warningStatus', field('warning_status', showUnlicensedToggleDecoder));

export const learningCollectionEditFormResourceDecoder: Decoder<LearningCollectionEditFormResource> =
  resourceDecoder(learningCollectionEditFormPayloadDecoder).assign('templates', ({ links }) =>
    field('templates', formTemplatesDecoder(links)),
  );
